import {
	ColumnDef,
	ExpandedState,
	SortingState,
	VisibilityState,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	getExpandedRowModel,
	useReactTable,
	RowSelectionState,
	Row,
} from '@tanstack/react-table';

import { useState, useCallback } from 'react';
import { ColumnSizeOptions, PaginationOptions } from './Table.interfaces';
import {
	TableViewState,
	useTableViewStateContext,
} from './TableViewStateContextProvider';

interface UseTableProps<T> {
	data: Array<T>;
	columns: ColumnDef<T, any>[];
	withFilters: boolean;
	withSearch: boolean;
	withPagination: boolean;
	withSelection?: boolean | ((row: Row<T>) => boolean);
	paginationOptions?: PaginationOptions;
	columnSizeOptions?: ColumnSizeOptions;
	initialSorting?: SortingState;
	enableSortingRemoval?: boolean;
	columnVisibility: VisibilityState;
	withExpandableRows?: boolean;
	tableId?: string;
}

function useTable<T>({
	data,
	columns,
	withFilters,
	withPagination,
	paginationOptions,
	columnSizeOptions,
	initialSorting,
	withSearch,
	enableSortingRemoval = true,
	columnVisibility,
	withExpandableRows = false,
	withSelection = false,
	tableId,
}: UseTableProps<T>) {
	const { getViewState, updateViewState } = useTableViewStateContext<T>();
	const viewState = getViewState?.(tableId);

	const [sorting, setSorting] = useState<SortingState>(
		viewState?.sorting ?? initialSorting ?? [],
	);
	const [globalFilter, setGlobalFilter] = useState<string | number>('');
	const [expanded, setExpanded] = useState<ExpandedState>({});
	const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

	const handleSortingChange = useCallback(
		(updater: any) => {
			setSorting((prev) => {
				const newSorting =
					typeof updater === 'function' ? updater(prev) : updater;

				if (newSorting.length > 0 && updateViewState) {
					updateViewState(tableId, { sorting: newSorting });
				}

				return newSorting;
			});
		},
		[tableId, updateViewState],
	);

	const table = useReactTable({
		data,
		columns,
		state: {
			sorting,
			globalFilter,
			columnVisibility: columnVisibility,
			expanded: withExpandableRows ? expanded : {},
			rowSelection: withSelection ? rowSelection : {},
		},
		defaultColumn: {
			minSize: columnSizeOptions?.minSize ?? 50,
			maxSize: columnSizeOptions?.maxSize ?? 150,
		},
		initialState: getInitialState(paginationOptions, viewState),
		onSortingChange: handleSortingChange,
		onGlobalFilterChange:
			withFilters || withSearch ? setGlobalFilter : undefined,
		getFilteredRowModel:
			withFilters || withSearch ? getFilteredRowModel() : undefined,
		getColumnCanGlobalFilter:
			withFilters || withSearch ? () => true : undefined,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: withPagination
			? getPaginationRowModel()
			: undefined,
		enableSortingRemoval: enableSortingRemoval,
		getExpandedRowModel: withExpandableRows
			? getExpandedRowModel()
			: undefined,
		onExpandedChange: withExpandableRows ? setExpanded : undefined,
		onRowSelectionChange: withSelection ? setRowSelection : undefined,
		meta: { tableId: tableId },
		enableMultiRowSelection: false,
		enableRowSelection: withSelection,
	});

	return { table, sorting };
}

function getInitialState<T>(
	paginationSettings?: PaginationOptions,
	initialTableViewState?: TableViewState<T>,
) {
	const state = {
		pagination: {},
		globalFilter: '',
	};

	if (initialTableViewState) {
		state.pagination = {
			pageSize: initialTableViewState.pageSize || 10,
			pageIndex: initialTableViewState.pageIndex || 0,
		};
	} else if (paginationSettings) {
		state.pagination = {
			pageSize: paginationSettings.initialPageSize || 10,
			pageIndex: 0,
		};
	}

	return state;
}

export default useTable;
